/**
 * 基本资料 Tab 组件
 */

import React, { useState, useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { userApi, formatDate } from '../../services/mongoUserApi';

const BasicInfoTab = ({ user, onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nickname: user.profile?.nickname || user.username || '',
    gender: user.profile?.gender || 'unknown',
    birthdate: user.profile?.birthdate || '',
    location: user.profile?.location || '',
    bio: user.profile?.bio || '',
  });

  const userProfile = user.profile || {};
  const userStatus = user.status || {};
  const userSecurity = user.security || {};

  // 性别选项
  const genderOptions = [
    { value: 'unknown', label: '未设置' },
    { value: 'male', label: '男' },
    { value: 'female', label: '女' },
    { value: 'other', label: '其他' },
  ];

  // 处理表单变更
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 开始编辑
  const handleStartEdit = () => {
    setIsEditing(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      nickname: userProfile.nickname || user.username || '',
      gender: userProfile.gender || 'unknown',
      birthdate: userProfile.birthdate || '',
      location: userProfile.location || '',
      bio: userProfile.bio || '',
    });
  };

  // 头像点击处理
  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 头像文件选择处理
  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 检查文件类型
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSaveError('仅支持 PNG、JPG、GIF、WebP 格式的图片');
      return;
    }

    // 检查文件大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('文件大小不能超过 5MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      setSaveError(null);

      const formData = new FormData();
      formData.append('avatar', file);

      const result = await userApi.uploadAvatar(user.user_id, formData);

      if (result.success) {
        setSaveSuccess(true);
        // 刷新用户信息
        if (onUpdate) {
          await onUpdate();
        }
        // 3秒后隐藏成功提示
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.message || '头像上传失败');
      }
    } catch (err) {
      console.error('头像上传失败:', err);
      setSaveError(err.response?.data?.message || '头像上传失败，请稍后重试');
    } finally {
      setUploadingAvatar(false);
      // 清空文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 保存资料
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      console.log('正在保存资料:', formData);
      const result = await userApi.updateProfile(user.user_id, formData);
      console.log('保存结果:', result);

      if (result.success) {
        setSaveSuccess(true);
        setIsEditing(false);
        
        // 调用父组件刷新（会更新全局状态和本地状态）
        if (onUpdate) {
          await onUpdate();
        }
        
        // 3秒后隐藏成功提示
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.message || '保存失败');
      }
    } catch (err) {
      console.error('保存资料失败:', err);
      setSaveError('网络错误，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  // 获取性别显示文本
  const getGenderLabel = (value) => {
    const option = genderOptions.find((opt) => opt.value === value);
    return option ? option.label : '未设置';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 成功提示 */}
      {saveSuccess && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ borderRadius: 2 }}
        >
          资料保存成功！
        </Alert>
      )}

      {/* 错误提示 */}
      {saveError && (
        <Alert
          severity="error"
          icon={<ErrorIcon fontSize="inherit" />}
          sx={{ borderRadius: 2 }}
        >
          {saveError}
        </Alert>
      )}

      {/* 基本资料卡片 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: isMobile ? 2 : 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            基本资料
          </Typography>
          {!isEditing ? (
            <Button
              startIcon={<EditIcon />}
              onClick={handleStartEdit}
              size="small"
              color="primary"
            >
              编辑
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                size="small"
                color="inherit"
              >
                取消
              </Button>
              <Button
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                size="small"
                color="primary"
                variant="contained"
                disabled={saving}
              >
                保存
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {/* 头像区域 —— 仅编辑态展示可编辑头像；非编辑态隐藏（顶部 Header 已展示头像） */}
          {isEditing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={userProfile.avatar}
                  sx={{
                    width: isMobile ? 100 : 120,
                    height: isMobile ? 100 : 120,
                    fontSize: isMobile ? 40 : 50,
                    border: '4px solid',
                    borderColor: 'primary.main',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      boxShadow: 3,
                    },
                  }}
                  onClick={handleAvatarClick}
                />
                {/* 编辑模式下显示相机图标 */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    boxShadow: 2,
                  }}
                  size="small"
                  onClick={handleAvatarClick}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PhotoCameraIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>
          )}
          {/* 隐藏的文件输入 —— 始终挂载，避免 ref 丢失（不影响视觉） */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />

          {/* 提示文字 */}
          {isEditing && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', display: 'block', mb: 2 }}
            >
              点击头像可上传新头像（支持 PNG、JPG、GIF、WebP，最大 5MB）
            </Typography>
          )}

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* 昵称 */}
            <Grid item xs={12} sm={6}>
              {isEditing ? (
                <TextField
                  fullWidth
                  label="昵称"
                  value={formData.nickname}
                  onChange={(e) => handleChange('nickname', e.target.value)}
                  size="small"
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    昵称
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {userProfile.nickname || user.username || '-'}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* 用户名（只读） */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  用户名
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {user.username || '-'}
                </Typography>
              </Box>
            </Grid>

            {/* 邮箱（只读） */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  邮箱
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body1">
                    {user.email || '-'}
                  </Typography>
                  {user.email_verified && (
                    <Chip
                      label="已验证"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* 手机号 */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  手机号
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body1">
                    {user.phone || '未绑定'}
                  </Typography>
                  {user.phone_verified && (
                    <Chip
                      label="已验证"
                      size="small"
                      color="success"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* 性别 */}
            <Grid item xs={12} sm={6}>
              {isEditing ? (
                <TextField
                  fullWidth
                  select
                  label="性别"
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  size="small"
                  SelectProps={{
                    native: true,
                  }}
                >
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    性别
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {getGenderLabel(userProfile.gender)}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* 生日 */}
            <Grid item xs={12} sm={6}>
              {isEditing ? (
                <TextField
                  fullWidth
                  label="生日"
                  type="date"
                  value={formData.birthdate}
                  onChange={(e) => handleChange('birthdate', e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    生日
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {userProfile.birthdate || '-'}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* 地区 */}
            <Grid item xs={12} sm={6}>
              {isEditing ? (
                <TextField
                  fullWidth
                  label="地区"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  size="small"
                  placeholder="例如：北京市朝阳区"
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    地区
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {userProfile.location || '-'}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* 时区（只读） */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  时区
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {userProfile.timezone || 'Asia/Shanghai'}
                </Typography>
              </Box>
            </Grid>

            {/* 个人简介 */}
            <Grid item xs={12}>
              {isEditing ? (
                <TextField
                  fullWidth
                  label="个人简介"
                  multiline
                  rows={4}
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  placeholder="介绍一下自己吧..."
                />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    个人简介
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {userProfile.bio || '这个人很懒，什么都没留下~'}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 账号状态卡片 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            账号状态
          </Typography>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  注册时间
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatDate(user.created_at)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  最后登录时间
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatDate(userSecurity.last_login_at)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  最后登录IP
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {userSecurity.last_login_ip || '-'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  账号状态
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {userStatus.is_active ? (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label="正常"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                      label="已禁用"
                      size="small"
                      color="error"
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default BasicInfoTab;
